import { useEffect, useRef, useState } from 'react'
import {
  Award,
  BarChart3,
  ImagePlus,
  Info,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  Trophy,
  Upload,
  UserRound,
} from 'lucide-react'
import { schoolsApi, uploadApi } from '../api/services.js'
import { useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import PageHeader from '../components/PageHeader.jsx'

const emptyLanding = {
  gallery: [],
  principal: {
    name: '',
    qualification: '',
    message: '',
    photo: '',
  },
  toppers: [],
  achievements: [],
  stats: {
    totalStudents: 0,
    totalTeachers: 0,
    established: '',
    passPercentage: '',
  },
}

const ACHIEVEMENT_ICONS = [
  { value: 'trophy-outline', label: 'Trophy' },
  { value: 'ribbon-outline', label: 'Ribbon' },
  { value: 'star-outline', label: 'Star' },
  { value: 'medal-outline', label: 'Medal' },
  { value: 'school-outline', label: 'School' },
  { value: 'book-outline', label: 'Book' },
  { value: 'flask-outline', label: 'Science' },
  { value: 'football-outline', label: 'Sports' },
  { value: 'musical-notes-outline', label: 'Music' },
  { value: 'brush-outline', label: 'Arts' },
  { value: 'globe-outline', label: 'Global' },
  { value: 'rocket-outline', label: 'Innovation' },
]

const uid = () => Math.random().toString(36).slice(2, 9)

const normalizeLanding = (data) => ({
  gallery: Array.isArray(data?.gallery)
    ? data.gallery.map((item) => ({
      _id: item?._id || uid(),
      url: item?.url || '',
      caption: item?.caption || '',
    }))
    : [],
  principal: {
    name: data?.principal?.name || '',
    qualification: data?.principal?.qualification || '',
    message: data?.principal?.message || '',
    photo: data?.principal?.photo || '',
  },
  toppers: Array.isArray(data?.toppers)
    ? data.toppers.map((item) => ({
      _id: item?._id || uid(),
      name: item?.name || '',
      class: item?.class || '',
      score: item?.score != null ? String(item.score) : '',
    }))
    : [],
  achievements: Array.isArray(data?.achievements)
    ? data.achievements.map((item) => ({
      _id: item?._id || uid(),
      title: item?.title || '',
      description: item?.description || '',
      year: item?.year || '',
      icon: item?.icon || 'star-outline',
    }))
    : [],
  stats: {
    totalStudents: Number(data?.stats?.totalStudents || 0),
    totalTeachers: Number(data?.stats?.totalTeachers || 0),
    established: data?.stats?.established != null ? String(data.stats.established) : '',
    passPercentage: data?.stats?.passPercentage != null ? String(data.stats.passPercentage) : '',
  },
})

export default function SchoolLandingPage() {
  const { user } = useSelector((s) => s.auth)
  const schoolId = user?.schoolId?._id || user?.schoolId || user?.school?._id || user?.school || ''
  const principalInputRef = useRef(null)
  const galleryInputRef = useRef(null)

  const [landing, setLanding] = useState(emptyLanding)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [savingSection, setSavingSection] = useState('')

  const fetchLanding = async ({ silent = false } = {}) => {
    if (!schoolId) {
      setLoading(false)
      return
    }

    try {
      if (silent) setRefreshing(true)
      else setLoading(true)
      const data = await schoolsApi.getLanding(schoolId)
      setLanding(normalizeLanding(data))
    } catch {
      toast.error('Failed to load landing page settings')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchLanding()
  }, [schoolId])

  const saveSection = async (section, body, successMessage) => {
    if (!schoolId) return

    try {
      setSavingSection(section)
      await schoolsApi.updateLanding(schoolId, body)
      toast.success(successMessage)
      await fetchLanding({ silent: true })
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save landing content')
    } finally {
      setSavingSection('')
    }
  }

  const uploadFile = async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const res = await uploadApi.uploadFile(formData)
    return res?.fileUrl || ''
  }

  const handlePrincipalPhotoChange = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    try {
      setSavingSection('principal-photo')
      const fileUrl = await uploadFile(file)
      setLanding((prev) => ({
        ...prev,
        principal: { ...prev.principal, photo: fileUrl },
      }))
      toast.success('Principal photo uploaded')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload principal photo')
    } finally {
      setSavingSection('')
    }
  }

  const handleGalleryUpload = async (event) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (files.length === 0) return

    try {
      setSavingSection('gallery-upload')
      const uploaded = await Promise.all(files.map(uploadFile))
      setLanding((prev) => ({
        ...prev,
        gallery: [
          ...prev.gallery,
          ...uploaded
            .filter(Boolean)
            .map((url) => ({ _id: uid(), url, caption: '' })),
        ],
      }))
      toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} uploaded`)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload gallery images')
    } finally {
      setSavingSection('')
    }
  }

  const updateGalleryItem = (id, key, value) => {
    setLanding((prev) => ({
      ...prev,
      gallery: prev.gallery.map((item) => (
        item._id === id ? { ...item, [key]: value } : item
      )),
    }))
  }

  const removeGalleryItem = (id) => {
    setLanding((prev) => ({
      ...prev,
      gallery: prev.gallery.filter((item) => item._id !== id),
    }))
  }

  const addTopper = () => {
    setLanding((prev) => ({
      ...prev,
      toppers: [...prev.toppers, { _id: uid(), name: '', class: '', score: '' }],
    }))
  }

  const updateTopper = (id, key, value) => {
    setLanding((prev) => ({
      ...prev,
      toppers: prev.toppers.map((item) => (
        item._id === id ? { ...item, [key]: value } : item
      )),
    }))
  }

  const removeTopper = (id) => {
    setLanding((prev) => ({
      ...prev,
      toppers: prev.toppers.filter((item) => item._id !== id),
    }))
  }

  const addAchievement = () => {
    setLanding((prev) => ({
      ...prev,
      achievements: [
        ...prev.achievements,
        {
          _id: uid(),
          title: '',
          description: '',
          year: String(new Date().getFullYear()),
          icon: 'star-outline',
        },
      ],
    }))
  }

  const updateAchievement = (id, key, value) => {
    setLanding((prev) => ({
      ...prev,
      achievements: prev.achievements.map((item) => (
        item._id === id ? { ...item, [key]: value } : item
      )),
    }))
  }

  const removeAchievement = (id) => {
    setLanding((prev) => ({
      ...prev,
      achievements: prev.achievements.filter((item) => item._id !== id),
    }))
  }

  const handleSaveStats = async () => {
    await saveSection(
      'stats',
      {
        stats: {
          established: landing.stats.established ? Number(landing.stats.established) : null,
          passPercentage: landing.stats.passPercentage ? Number(landing.stats.passPercentage) : null,
        },
      },
      'School stats updated'
    )
  }

  const handleSavePrincipal = async () => {
    if (!landing.principal.name.trim()) {
      toast.error('Principal name is required')
      return
    }

    await saveSection(
      'principal',
      {
        principal: {
          name: landing.principal.name.trim(),
          qualification: landing.principal.qualification.trim(),
          message: landing.principal.message.trim(),
          photo: landing.principal.photo || undefined,
        },
      },
      'Principal details updated'
    )
  }

  const handleSaveGallery = async () => {
    const invalid = landing.gallery.some((item) => !item.url)
    if (invalid) {
      toast.error('Wait for image uploads to finish before saving the gallery')
      return
    }

    await saveSection(
      'gallery',
      {
        gallery: landing.gallery.map(({ _id, ...item }) => ({
          url: item.url,
          caption: item.caption?.trim() || '',
        })),
      },
      'Gallery updated'
    )
  }

  const handleSaveToppers = async () => {
    const hasIncomplete = landing.toppers.some((item) => !item.name.trim() || !item.class.trim() || !item.score.trim())
    if (hasIncomplete) {
      toast.error('Fill in name, class, and score for every topper')
      return
    }

    await saveSection(
      'toppers',
      {
        toppers: landing.toppers.map(({ _id, name, class: className, score }) => ({
          name: name.trim(),
          class: className.trim(),
          score: Number(score),
        })),
      },
      'Toppers updated'
    )
  }

  const handleSaveAchievements = async () => {
    const hasIncomplete = landing.achievements.some((item) => !item.title.trim())
    if (hasIncomplete) {
      toast.error('Every achievement needs a title')
      return
    }

    await saveSection(
      'achievements',
      {
        achievements: landing.achievements.map(({ _id, title, description, year, icon }) => ({
          title: title.trim(),
          description: description.trim(),
          year: year.trim(),
          icon,
        })),
      },
      'Achievements updated'
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-7 h-7 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Landing Page Editor"
        subtitle="This now follows the same BrainCamp admin landing editor structure: stats, gallery, principal message, toppers, and achievements."
        chips={[
          `${landing.stats.totalStudents || 0} students`,
          `${landing.stats.totalTeachers || 0} teachers`,
          `${landing.gallery.length} gallery photo${landing.gallery.length !== 1 ? 's' : ''}`,
          `${landing.toppers.length} topper${landing.toppers.length !== 1 ? 's' : ''}`,
        ]}
        actions={
          <button onClick={() => fetchLanding({ silent: true })} className="btn-secondary" disabled={refreshing}>
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        }
      />

      <input
        ref={principalInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handlePrincipalPhotoChange}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleGalleryUpload}
      />

      <div className="soft-panel p-5">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {[
            { label: 'School Stats', value: landing.stats.established || 'Not set', note: 'Established year', icon: BarChart3, tone: 'bg-blue-50 text-blue-600' },
            { label: 'Pass Rate', value: landing.stats.passPercentage ? `${landing.stats.passPercentage}%` : 'Not set', note: 'Landing page highlight', icon: BarChart3, tone: 'bg-emerald-50 text-emerald-600' },
            { label: 'Gallery', value: landing.gallery.length, note: 'Photos on landing page', icon: ImagePlus, tone: 'bg-amber-50 text-amber-600' },
            { label: 'Principal', value: landing.principal.name || 'Not set', note: 'Principal’s desk section', icon: UserRound, tone: 'bg-violet-50 text-violet-600' },
            { label: 'Achievements', value: landing.achievements.length, note: 'Highlight cards', icon: Award, tone: 'bg-rose-50 text-rose-600' },
          ].map(({ label, value, note, icon: Icon, tone }) => (
            <div key={label} className="rounded-2xl border border-gray-100 bg-white px-4 py-4 shadow-sm">
              <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${tone}`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-sm text-gray-500 mt-4">{label}</p>
              <p className="text-lg font-bold text-gray-900 mt-1 break-words">{value}</p>
              <p className="text-xs text-gray-400 mt-1">{note}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="card p-6 space-y-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">School Stats</p>
          <h3 className="text-xl font-bold text-gray-900 mt-2">Key numbers shown on the landing page</h3>
          <p className="text-sm text-gray-500 mt-2">Total students and teachers are pulled from the database automatically, just like the RN editor.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Established Year</label>
            <input
              className="input-field w-full"
              value={landing.stats.established}
              onChange={(e) => setLanding((prev) => ({
                ...prev,
                stats: { ...prev.stats, established: e.target.value },
              }))}
              placeholder="e.g. 1995"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Pass Percentage (%)</label>
            <input
              className="input-field w-full"
              value={landing.stats.passPercentage}
              onChange={(e) => setLanding((prev) => ({
                ...prev,
                stats: { ...prev.stats, passPercentage: e.target.value },
              }))}
              placeholder="e.g. 98"
            />
          </div>
        </div>

        <div className="rounded-2xl border border-primary-100 bg-primary-50 px-4 py-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-primary-700">
              <p className="font-semibold">Auto-computed stats</p>
              <p className="mt-1">Students: {landing.stats.totalStudents || 0} · Teachers: {landing.stats.totalTeachers || 0}</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={handleSaveStats} className="btn-primary" disabled={savingSection === 'stats'}>
            {savingSection === 'stats' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Stats
          </button>
        </div>
      </div>

      <div className="card p-6 space-y-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">School Gallery</p>
            <h3 className="text-xl font-bold text-gray-900 mt-2">Photos shown in the landing carousel</h3>
            <p className="text-sm text-gray-500 mt-2">This replaces the old hero-image-only form with the same gallery-driven setup used in RN.</p>
          </div>
          <button onClick={() => galleryInputRef.current?.click()} className="btn-secondary" disabled={savingSection === 'gallery-upload'}>
            {savingSection === 'gallery-upload' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Add Photo
          </button>
        </div>

        {landing.gallery.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
            <ImagePlus className="w-8 h-8 text-gray-300 mx-auto" />
            <p className="text-sm font-semibold text-gray-700 mt-4">No gallery photos yet</p>
            <p className="text-sm text-gray-500 mt-2">Upload images to build the school landing carousel.</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {landing.gallery.map((item, index) => (
              <div key={item._id} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <div className="aspect-[16/10] overflow-hidden rounded-2xl bg-gray-100">
                  {item.url ? (
                    <img src={item.url} alt={item.caption || `Gallery ${index + 1}`} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <ImagePlus className="w-6 h-6" />
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between gap-3 mt-4">
                  <p className="text-sm font-semibold text-gray-900">Photo {index + 1}</p>
                  <button onClick={() => removeGalleryItem(item._id)} className="rounded-xl border border-red-200 bg-white px-2.5 py-1.5 text-red-500 transition-colors hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <input
                  className="input-field w-full mt-3"
                  value={item.caption}
                  onChange={(e) => updateGalleryItem(item._id, 'caption', e.target.value)}
                  placeholder="Caption (optional)"
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end">
          <button onClick={handleSaveGallery} className="btn-primary" disabled={savingSection === 'gallery'}>
            {savingSection === 'gallery' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Gallery
          </button>
        </div>
      </div>

      <div className="card p-6 space-y-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Principal’s Desk</p>
          <h3 className="text-xl font-bold text-gray-900 mt-2">Name, photo, and welcome message</h3>
        </div>

        <div className="grid gap-5 xl:grid-cols-[260px_minmax(0,1fr)]">
          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-5 text-center flex flex-col items-center justify-center min-h-[280px]">
            {landing.principal.photo ? (
              <img src={landing.principal.photo} alt={landing.principal.name || 'Principal'} className="w-36 h-36 rounded-3xl object-cover shadow-sm" />
            ) : (
              <div className="w-24 h-24 rounded-3xl bg-white flex items-center justify-center shadow-sm">
                <UserRound className="w-10 h-10 text-gray-300" />
              </div>
            )}
            <button onClick={() => principalInputRef.current?.click()} className="btn-secondary mt-5" disabled={savingSection === 'principal-photo'}>
              {savingSection === 'principal-photo' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {landing.principal.photo ? 'Change Photo' : 'Upload Photo'}
            </button>
            {landing.principal.photo && (
              <button
                onClick={() => setLanding((prev) => ({ ...prev, principal: { ...prev.principal, photo: '' } }))}
                className="text-sm font-medium text-red-500 mt-3 hover:text-red-600"
              >
                Remove photo
              </button>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Principal Name *</label>
              <input
                className="input-field w-full"
                value={landing.principal.name}
                onChange={(e) => setLanding((prev) => ({
                  ...prev,
                  principal: { ...prev.principal, name: e.target.value },
                }))}
                placeholder="e.g. Dr. Rajesh Kumar"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Qualification</label>
              <input
                className="input-field w-full"
                value={landing.principal.qualification}
                onChange={(e) => setLanding((prev) => ({
                  ...prev,
                  principal: { ...prev.principal, qualification: e.target.value },
                }))}
                placeholder="e.g. M.Ed., PhD"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Welcome Message</label>
              <textarea
                className="input-field w-full h-36 resize-none"
                value={landing.principal.message}
                onChange={(e) => setLanding((prev) => ({
                  ...prev,
                  principal: { ...prev.principal, message: e.target.value },
                }))}
                placeholder="Write the message that appears on the landing page."
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={handleSavePrincipal} className="btn-primary" disabled={savingSection === 'principal'}>
            {savingSection === 'principal' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Principal Section
          </button>
        </div>
      </div>

      <div className="card p-6 space-y-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Top Performers</p>
            <h3 className="text-xl font-bold text-gray-900 mt-2">Toppers shown on the school landing page</h3>
          </div>
          <button onClick={addTopper} className="btn-secondary">
            <Plus className="w-4 h-4" />
            Add Topper
          </button>
        </div>

        {landing.toppers.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
            <Trophy className="w-8 h-8 text-gray-300 mx-auto" />
            <p className="text-sm font-semibold text-gray-700 mt-4">No toppers added yet</p>
            <p className="text-sm text-gray-500 mt-2">Add high-performing students to highlight them on the landing page.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {landing.toppers.map((item, index) => (
              <div key={item._id} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-gray-900">Topper {index + 1}</p>
                  <button onClick={() => removeTopper(item._id)} className="rounded-xl border border-red-200 bg-white px-2.5 py-1.5 text-red-500 transition-colors hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid gap-4 md:grid-cols-3 mt-4">
                  <input className="input-field w-full" value={item.name} onChange={(e) => updateTopper(item._id, 'name', e.target.value)} placeholder="Student name" />
                  <input className="input-field w-full" value={item.class} onChange={(e) => updateTopper(item._id, 'class', e.target.value)} placeholder="Class section" />
                  <input className="input-field w-full" value={item.score} onChange={(e) => updateTopper(item._id, 'score', e.target.value)} placeholder="Score or percentage" />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end">
          <button onClick={handleSaveToppers} className="btn-primary" disabled={savingSection === 'toppers'}>
            {savingSection === 'toppers' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Toppers
          </button>
        </div>
      </div>

      <div className="card p-6 space-y-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Achievements</p>
            <h3 className="text-xl font-bold text-gray-900 mt-2">School achievements shown as highlight cards</h3>
          </div>
          <button onClick={addAchievement} className="btn-secondary">
            <Plus className="w-4 h-4" />
            Add Achievement
          </button>
        </div>

        {landing.achievements.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
            <Award className="w-8 h-8 text-gray-300 mx-auto" />
            <p className="text-sm font-semibold text-gray-700 mt-4">No achievements added yet</p>
            <p className="text-sm text-gray-500 mt-2">Add awards, milestones, or recognitions to build social proof on the landing page.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {landing.achievements.map((item, index) => (
              <div key={item._id} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-gray-900">Achievement {index + 1}</p>
                  <button onClick={() => removeAchievement(item._id)} className="rounded-xl border border-red-200 bg-white px-2.5 py-1.5 text-red-500 transition-colors hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_140px_180px] mt-4">
                  <input className="input-field w-full" value={item.title} onChange={(e) => updateAchievement(item._id, 'title', e.target.value)} placeholder="Achievement title" />
                  <input className="input-field w-full" value={item.year} onChange={(e) => updateAchievement(item._id, 'year', e.target.value)} placeholder="Year" />
                  <select className="input-field w-full" value={item.icon} onChange={(e) => updateAchievement(item._id, 'icon', e.target.value)}>
                    {ACHIEVEMENT_ICONS.map((icon) => (
                      <option key={icon.value} value={icon.value}>{icon.label}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  className="input-field w-full h-24 resize-none mt-4"
                  value={item.description}
                  onChange={(e) => updateAchievement(item._id, 'description', e.target.value)}
                  placeholder="Short description for this achievement"
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end">
          <button onClick={handleSaveAchievements} className="btn-primary" disabled={savingSection === 'achievements'}>
            {savingSection === 'achievements' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Achievements
          </button>
        </div>
      </div>
    </div>
  )
}
